import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { logHouseAdClick, logHouseAdImpression } from '../analyticsHelper';

export const HOUSE_PROMO_SECONDS = 4;

export interface HouseAdCampaign {
  id: string;
  enabled: boolean;
  platform: string;
  headline: string;
  message: string;
  cta: string;
  url: string;
  accent?: string;
  imageUrl?: string;
  videoUrl?: string;
}

const FALLBACK_CAMPAIGN: HouseAdCampaign = {
  id: 'nebula-maze-play',
  enabled: true,
  platform: 'Nebula Maze',
  headline: 'Find your path. Challenge your wits.',
  message: 'Progress through mazes, answer challenges and earn your score.',
  cta: 'Play now',
  url: 'https://nebula-maze.web.app/',
  accent: '#22d3ee',
};

const LOCAL_TEST_MEDIA: Record<string, Pick<HouseAdCampaign, 'imageUrl' | 'videoUrl'>> = {
  'missing-lecture-college-prep': {
    imageUrl: '/__house-ad-preview/missing-lecture.png',
    videoUrl: '/__house-ad-preview/missing-lecture.mp4',
  },
  'diskava-beat-awards': {
    imageUrl: '/__house-ad-preview/diskava-beats.png',
    videoUrl: '/__house-ad-preview/diskava-beats.mp4',
  },
  'herbalog-coming-soon': {
    imageUrl: '/__house-ad-preview/herbalog.png',
    videoUrl: '/__house-ad-preview/herbalog.mp4',
  },
};

export const useHouseAds = () => {
  const [campaigns, setCampaigns] = useState<HouseAdCampaign[]>([FALLBACK_CAMPAIGN]);

  useEffect(() => {
    let active = true;
    fetch('/house-ads.json', { cache: 'no-store' })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('Campaign file unavailable')))
      .then((items: HouseAdCampaign[]) => {
        const localTestId = import.meta.env.DEV
          ? new URLSearchParams(window.location.search).get('houseAdTest')
          : null;
        const localTestCampaigns = items
          .filter((item) => LOCAL_TEST_MEDIA[item.id] && /^https?:\/\//i.test(item.url))
          .map((item) => ({ ...item, ...LOCAL_TEST_MEDIA[item.id] }));
        const selectedIndex = localTestId
          ? localTestCampaigns.findIndex((item) => item.id === localTestId)
          : -1;
        if (active && selectedIndex >= 0) {
          setCampaigns([
            ...localTestCampaigns.slice(selectedIndex),
            ...localTestCampaigns.slice(0, selectedIndex),
          ]);
          return;
        }
        const enabled = items.filter((item) => item.enabled && /^https?:\/\//i.test(item.url));
        if (active && enabled.length > 0) setCampaigns(enabled);
      })
      .catch(() => {
        // The built-in Nebula Maze campaign remains available offline.
      });
    return () => { active = false; };
  }, []);

  return campaigns;
};

interface HouseAdProps {
  variant: 'ticker' | 'rail' | 'card';
  campaignIndex?: number;
  placement: string;
  className?: string;
  campaignOverride?: HouseAdCampaign;
  preview?: boolean;
}

const HouseAdVideo: React.FC<{ campaign: HouseAdCampaign }> = ({ campaign }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    video.load();
    video.play().catch(() => {
      // The poster remains visible if a browser blocks muted autoplay.
    });
    const timer = window.setTimeout(() => video.pause(), HOUSE_PROMO_SECONDS * 1000);
    return () => window.clearTimeout(timer);
  }, [campaign.videoUrl]);

  return (
    <video
      ref={videoRef}
      src={campaign.videoUrl}
      poster={campaign.imageUrl}
      aria-label={`${campaign.platform}: ${campaign.headline}`}
      className="mb-3 w-full max-h-[58vh] rounded-lg bg-slate-950 object-contain"
      autoPlay
      muted
      playsInline
      preload="auto"
    />
  );
};

export const HouseAd: React.FC<HouseAdProps> = ({
  variant,
  campaignIndex = 0,
  placement,
  className = '',
  campaignOverride,
  preview = false,
}) => {
  const campaigns = useHouseAds();
  const campaign = useMemo(
    () => campaignOverride || campaigns[campaignIndex % campaigns.length] || FALLBACK_CAMPAIGN,
    [campaignIndex, campaignOverride, campaigns]
  );

  useEffect(() => {
    if (preview) return;
    logHouseAdImpression(campaign.id, campaign.platform, placement);
  }, [campaign.id, campaign.platform, placement, preview]);

  const handleClick = () => {
    if (preview) return;
    logHouseAdClick(campaign.id, campaign.platform, placement);
  };

  if (variant === 'ticker') {
    return (
      <a
        href={campaign.url}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={handleClick}
        className={`w-full h-[30px] bg-slate-950 border-t border-slate-700 flex items-center overflow-hidden flex-shrink-0 ${className}`}
        aria-label={`${campaign.platform}: ${campaign.headline}`}
      >
        <div className="house-ad-ticker whitespace-nowrap text-xs font-bold uppercase tracking-wide flex items-center gap-8" style={{ color: campaign.accent || '#22d3ee' }}>
          <span>Sponsored · {campaign.platform}</span><span>•</span><span>{campaign.headline}</span><span>•</span><span>{campaign.cta}</span>
          <span>Sponsored · {campaign.platform}</span><span>•</span><span>{campaign.headline}</span><span>•</span><span>{campaign.cta}</span>
        </div>
        <style>{`@keyframes houseAdScroll { from { transform: translateX(0); } to { transform: translateX(-50%); } } .house-ad-ticker { animation: houseAdScroll 14s linear infinite; }`}</style>
      </a>
    );
  }

  if (variant === 'rail') {
    return (
      <a
        href={campaign.url}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={handleClick}
        className={`w-full min-h-[250px] rounded-lg border bg-slate-950/80 px-1.5 py-3 flex flex-col items-center justify-between text-center hover:bg-slate-800 transition-colors ${className}`}
        style={{ borderColor: `${campaign.accent || '#22d3ee'}66` }}
        aria-label={`${campaign.platform}: ${campaign.headline}`}
      >
        <span className="text-[8px] uppercase tracking-widest text-slate-500">{preview ? 'Local preview' : 'Sponsored'}</span>
        {!preview && (
          <span className="text-[7px] font-bold uppercase leading-tight tracking-wide text-cyan-300">
            Ad spot available<br />Static or banner
          </span>
        )}
        {campaign.imageUrl ? (
          <img src={campaign.imageUrl} alt="" className="w-full rounded object-contain" />
        ) : (
          <span className="text-[10px] font-bold leading-tight" style={{ color: campaign.accent || '#22d3ee', writingMode: 'vertical-rl' }}>{campaign.platform}</span>
        )}
        <span className="text-[9px] font-semibold leading-tight text-white">{campaign.headline}</span>
        <span className="text-[9px] font-bold flex items-center gap-1" style={{ color: campaign.accent || '#22d3ee' }}>{campaign.cta}<ExternalLink className="w-2.5 h-2.5" /></span>
      </a>
    );
  }

  return (
    <a
      href={campaign.url}
      target="_blank"
      rel="noopener noreferrer sponsored"
      onClick={handleClick}
      className={`block rounded-xl border bg-slate-800/90 p-4 hover:bg-slate-800 transition-colors ${className}`}
      style={{ borderColor: `${campaign.accent || '#22d3ee'}88` }}
    >
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">{preview ? 'Local house promotion · not a revenue ad' : 'House promotion'} · {campaign.platform}</div>
      {campaign.videoUrl ? (
        <HouseAdVideo campaign={campaign} />
      ) : campaign.imageUrl && (
        <img
          src={campaign.imageUrl}
          alt={`${campaign.platform}: ${campaign.headline}`}
          className="mb-3 w-full max-h-[58vh] rounded-lg bg-slate-950 object-contain"
        />
      )}
      <div className="font-bold text-white">{campaign.headline}</div>
      <div className="text-xs text-slate-300 mt-1">{campaign.message}</div>
      <div className="text-xs font-bold mt-3 flex items-center gap-1" style={{ color: campaign.accent || '#22d3ee' }}>{campaign.cta}<ExternalLink className="w-3 h-3" /></div>
    </a>
  );
};
